import {
	Box,
	Button,
	FormHelperText,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import type { KeyboardEvent } from 'react';
import type { V2TableColumn, V2TableElement, V2TableRow } from '../../../types/V2Types';
import type { V2ElementRendererProps } from './elementProps';

/**
 * Swallow Enter inside any table cell input so it does NOT submit the
 * surrounding HRR form. A table is a multi-cell entry experience — pressing
 * Enter mid-row would otherwise advance to the Next page before the customer
 * has finished filling in the row. The customer must explicitly click Next.
 *
 * Cells remain natively editable; only the Enter→submit bubble is blocked.
 */
const swallowEnter = (event: KeyboardEvent<HTMLDivElement>) => {
	if (event.key === 'Enter') {
		event.preventDefault();
		event.stopPropagation();
	}
};

export function TableElement({
	element,
	value,
	errors,
	disabled,
	onChange,
}: V2ElementRendererProps<V2TableElement>) {
	const rows = (Array.isArray(value) ? value : []) as V2TableRow[];
	const errorMessage = errors[0]?.message;

	const updateCell = (rowIndex: number, columnId: string, newValue: string | number | null) => {
		const next = rows.map((r, i) =>
			i === rowIndex ? { ...r, cells: { ...r.cells, [columnId]: newValue } } : r,
		);
		onChange(next);
	};

	const addRow = () => {
		const next: V2TableRow = {
			rowId: `row-${Date.now()}-${rows.length}`,
			cells: Object.fromEntries(element.columns.map((c) => [c.columnId, null])),
		};
		onChange([...rows, next]);
	};

	const removeRow = (rowIndex: number) => {
		onChange(rows.filter((_, i) => i !== rowIndex));
	};

	const canAdd = !disabled && (element.maxRows === undefined || rows.length < element.maxRows);

	return (
		<Box
			sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
			data-cy={`hrr-element-${element.elementId}`}
			data-element-type="table">
			<Typography variant="subtitle1">
				{element.label}
				{element.required && ' *'}
			</Typography>
			{element.description && (
				<Typography variant="body2" color="text.secondary">
					{element.description}
				</Typography>
			)}

			<TableContainer component={Paper} variant="outlined">
				<Table size="small">
					<TableHead>
						<TableRow>
							{element.columns.map((c) => (
								<TableCell key={c.columnId}>{c.label}</TableCell>
							))}
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{rows.length === 0 && (
							<TableRow>
								<TableCell
									colSpan={element.columns.length + 1}
									align="center"
									sx={{ color: 'text.secondary' }}
									data-cy={`hrr-table-empty-${element.elementId}`}>
									No rows yet
								</TableCell>
							</TableRow>
						)}
						{rows.map((row, rowIndex) => (
							<TableRow key={row.rowId} data-cy={`hrr-table-row-${element.elementId}-${rowIndex}`}>
								{element.columns.map((column) => (
									<TableCell key={column.columnId}>
										<CellInput
											column={column}
											value={row.cells[column.columnId] ?? null}
											disabled={disabled}
											onChange={(v) => updateCell(rowIndex, column.columnId, v)}
										/>
									</TableCell>
								))}
								<TableCell align="right">
									<IconButton
										size="small"
										onClick={() => removeRow(rowIndex)}
										disabled={disabled}
										aria-label={`Remove row ${rowIndex + 1}`}>
										×
									</IconButton>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</TableContainer>

			<Button
				variant="text"
				disabled={!canAdd}
				onClick={addRow}
				data-cy={`hrr-table-add-${element.elementId}`}>
				+ Add row
			</Button>

			{errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
		</Box>
	);
}

interface CellInputProps {
	column: V2TableColumn;
	value: string | number | null;
	disabled?: boolean;
	onChange: (next: string | number | null) => void;
}

function CellInput({ column, value, disabled, onChange }: CellInputProps) {
	if (column.dataType === 'numeric') {
		return (
			<TextField
				size="small"
				type="number"
				value={value ?? ''}
				disabled={disabled}
				onChange={(e) => {
					const raw = e.target.value;
					if (raw === '') {
						onChange(null);
						return;
					}
					const n = Number(raw);
					onChange(Number.isFinite(n) ? n : raw);
				}}
				onKeyDown={swallowEnter}
				required={column.required}
			/>
		);
	}
	if (column.dataType === 'date') {
		return (
			<TextField
				size="small"
				type="date"
				value={(value as string) ?? ''}
				disabled={disabled}
				onChange={(e) => onChange(e.target.value || null)}
				onKeyDown={swallowEnter}
				required={column.required}
				InputLabelProps={{ shrink: true }}
			/>
		);
	}
	return (
		<TextField
			size="small"
			value={(value as string) ?? ''}
			disabled={disabled}
			onChange={(e) => onChange(e.target.value || null)}
			onKeyDown={swallowEnter}
			required={column.required}
		/>
	);
}
